import { useNavigate, useSearch } from '@tanstack/react-router';
import { z } from 'zod';

export const TABLE_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
export type TablePageSize = (typeof TABLE_PAGE_SIZE_OPTIONS)[number];

export const DEFAULT_PAGE = 1;
export const DEFAULT_PAGE_SIZE: TablePageSize = 20;

const pageSizeSchema = z.coerce
  .number()
  .int()
  .positive()
  .transform(
    (n): TablePageSize =>
      (TABLE_PAGE_SIZE_OPTIONS as readonly number[]).includes(n)
        ? (n as TablePageSize)
        : DEFAULT_PAGE_SIZE,
  );

export const paginationSchema = z.object({
  page: z.coerce
    .number()
    .int()
    .positive()
    .catch(DEFAULT_PAGE)
    .default(DEFAULT_PAGE),
  pageSize: pageSizeSchema.catch(DEFAULT_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
});

/** @deprecated Use `paginationSchema` */
export const tablePageSearchSchema = paginationSchema;

export type PaginationSearch = z.infer<typeof paginationSchema>;
export type TablePageSearch = PaginationSearch;

export function parseTablePage(
  search: Record<string, unknown>,
  pageKey = 'page',
): number {
  const raw = search[pageKey];
  const parsed = z.coerce.number().int().positive().safeParse(raw);
  return parsed.success ? parsed.data : DEFAULT_PAGE;
}

export function parseTablePageSize(
  search: Record<string, unknown>,
  pageSizeKey = 'pageSize',
): TablePageSize {
  return (
    pageSizeSchema.safeParse(search[pageSizeKey]).data ?? DEFAULT_PAGE_SIZE
  );
}

interface UseTablePaginationSearchOptions {
  pageKey?: string;
  pageSizeKey?: string;
}

/** Read and update `page` / `pageSize` search params for table pagination. */
export function useTablePaginationSearch({
  pageKey = 'page',
  pageSizeKey = 'pageSize',
}: UseTablePaginationSearchOptions = {}) {
  const search = useSearch({ strict: false }) as Record<string, unknown>;
  const navigate = useNavigate();
  const page = parseTablePage(search, pageKey);
  const pageSize = parseTablePageSize(search, pageSizeKey);

  const updateSearch = (updates: Record<string, unknown>) => {
    void navigate({
      to: '.',
      search: (prev) => ({
        ...(prev as Record<string, unknown>),
        ...updates,
      }),
      replace: true,
    });
  };

  const setPage = (nextPage: number) => {
    updateSearch({ [pageKey]: nextPage });
  };

  const setPageSize = (nextPageSize: TablePageSize) => {
    updateSearch({ [pageSizeKey]: nextPageSize, [pageKey]: DEFAULT_PAGE });
  };

  return { page, pageSize, setPage, setPageSize };
}
